import React, { useState } from 'react';
// Framer Motion supprimé - utilisation d'animations CSS
import { 
  Search, 
  Filter, 
  Download, 
  Plus, 
  Eye, 
  Edit, 
  Trash2,
  AlertCircle,
  MapPin
} from 'lucide-react';
import { useDiseases } from '../../hooks/useApi';
import { useDiseasesCrud } from '../../hooks/useDiseasesCrud';
import DiseaseAddModal from './DiseaseAddModal';
import DiseaseEditModal from './DiseaseEditModal';
import DiseaseDeleteModal from './DiseaseDeleteModal';
import { useNotification } from '../../hooks/useNotification';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const DiseasesList = () => {
  const { data: diseases = [], loading, error, refetch } = useDiseases();
  const { createDisease, updateDisease, deleteDisease: removeDisease, loading: crudLoading } = useDiseasesCrud();
  const { showNotification } = useNotification();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPrevalence, setFilterPrevalence] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDisease, setSelectedDisease] = useState(null);

  const prevalences = [...new Set(diseases.map(disease => disease.prevalence))];

  // Handlers
  const handleCreate = async (diseaseData) => {
    try {
      await createDisease(diseaseData);
      showNotification('Maladie créée avec succès', 'success');
      setShowAddModal(false);
      refetch();
    } catch (error) {
      showNotification('Erreur lors de la création: ' + error.message, 'error');
    }
  };

  const handleUpdate = async (id, diseaseData) => {
    try {
      await updateDisease(id, diseaseData);
      showNotification('Maladie mise à jour avec succès', 'success');
      setShowEditModal(false);
      setSelectedDisease(null);
      refetch();
    } catch (error) {
      showNotification('Erreur lors de la mise à jour: ' + error.message, 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await removeDisease(selectedDisease.id);
      showNotification('Maladie supprimée avec succès', 'success');
      setShowDeleteModal(false);
      setSelectedDisease(null);
      refetch();
    } catch (error) {
      showNotification('Erreur lors de la suppression: ' + error.message, 'error');
    }
  };

  const openEditModal = (disease) => {
    setSelectedDisease(disease);
    setShowEditModal(true);
  };

  const openDeleteModal = (disease) => {
    setSelectedDisease(disease);
    setShowDeleteModal(true);
  };

  const filteredDiseases = diseases.filter(disease => {
    if (!disease) return false;
    const matchesSearch = disease.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         disease.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         disease.zone?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = !filterPrevalence || disease.prevalence === filterPrevalence;
    return matchesSearch && matchesFilter;
  });

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Titre
    doc.setFontSize(20);
    doc.text('Répertoire des Maladies', 20, 20);
    
    // Tableau
    const tableData = filteredDiseases.map(disease => [
      disease.name,
      disease.description,
      disease.symptoms.join(', '),
      disease.treatment,
      disease.prevalence,
      disease.zone
    ]);

    autoTable(doc, {
      head: [['Nom', 'Description', 'Symptômes', 'Traitement', 'Prévalence', 'Zone']],
      body: tableData,
      startY: 30,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [239, 68, 68] }
    });

    doc.save('repertoire-maladies.pdf');
  };

  const getPrevalenceColor = (prevalence) => {
    switch (prevalence) {
      case 'Élevée':
        return 'bg-red-100 text-red-800';
      case 'Modérée':
        return 'bg-yellow-100 text-yellow-800';
      case 'Faible':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className=" px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div
          
          
          className="mb-8"
        >
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Répertoire des Maladies</h1>
              <p className="text-gray-600 mt-2">Base de données des maladies et leurs traitements</p>
            </div>
            <div className="flex space-x-3">
              <button
                
                
                onClick={exportToPDF}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
              >
                <Download className="h-4 w-4" />
                <span>Exporter PDF</span>
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
              >
                <Plus className="h-4 w-4" />
                <span>Nouvelle Maladie</span>
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div
            
            
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8 text-center"
          >
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Chargement des maladies...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div
            
            
            className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erreur de chargement</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filter */}
        {!loading && !error && (
        <div
          
          
          
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Rechercher une maladie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <select
                value={filterPrevalence}
                onChange={(e) => setFilterPrevalence(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Toutes les prévalences</option>
                {prevalences.map(prevalence => (
                  <option key={prevalence} value={prevalence}>{prevalence}</option>
                ))}
              </select>
            </div>
            <div className="text-sm text-gray-500 flex items-center">
              {filteredDiseases.length} maladie(s) trouvée(s)
            </div>
          </div>
        </div>
        )}

        {/* Diseases Grid */}
        {!loading && !error && (
        <div
          
          
          
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredDiseases.map((disease, index) => (
            <div
              key={disease.id}
              
              
              
              
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{disease.name || 'Sans nom'}</h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPrevalenceColor(disease.prevalence)}`}>
                      {disease.prevalence || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-gray-600 text-sm mb-4 line-clamp-2">{disease.description || 'Aucune description'}</p>

              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Symptômes</h4>
                  <div className="flex flex-wrap gap-1">
                    {disease.symptoms && Array.isArray(disease.symptoms) && disease.symptoms.length > 0 ? (
                      disease.symptoms.map((symptom, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {symptom}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-500">Aucun symptôme renseigné</span>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">Traitement</h4>
                  <p className="text-sm text-gray-600">{disease.treatment || 'Aucun traitement renseigné'}</p>
                </div>

                {disease.zone && (
                  <div className="flex items-center text-sm text-gray-500">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span>{disease.zone}</span>
                  </div>
                )}
              </div>

              <div className="mt-4 flex space-x-2">
                <button
                  onClick={() => openEditModal(disease)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg flex items-center justify-center space-x-2 transition-colors duration-200"
                >
                  <Edit className="h-4 w-4" />
                  <span>Modifier</span>
                </button>
                <button
                  onClick={() => openDeleteModal(disease)}
                  className="bg-red-100 hover:bg-red-200 text-red-700 py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
        )}

        {filteredDiseases.length === 0 && (
          <div
            
            
            className="text-center py-12"
          >
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune maladie trouvée</h3>
            <p className="text-gray-500">Essayez de modifier vos critères de recherche</p>
          </div>
        )}

        {/* Modals */}
        <DiseaseAddModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onCreate={handleCreate}
          loading={crudLoading}
        />

        <DiseaseEditModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedDisease(null);
          }}
          onUpdate={handleUpdate}
          disease={selectedDisease}
          loading={crudLoading}
        />

        <DiseaseDeleteModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedDisease(null);
          }}
          onConfirm={handleDelete}
          disease={selectedDisease}
          loading={crudLoading}
        />
      </div>
    </div>
  );
};

export default DiseasesList;
